import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import Layout from '../../components/Layout'
import Toast from '../../utils/toast'
import autoVoidService from '../../services/autoVoidService'
import { SPORTS_LIST } from '../../config/sportsList'

/**
 * Auto Void — owner-only (tenexch admin /settings/auto-void ka port, is panel ke theme me).
 *
 * Results cron (server/sportsbet/betresult/autoVoid.js) jab kisi event ka Match Odds /
 * Bookmaker result feed me declare dekhta hai to timer chalu karta hai. Delay ke baad us
 * event ki har 'open' bet jiska result feed ne nahi diya, void ho jaati hai.
 *
 * Page ke chaar hisse:
 *   Auto Void Settings     → ON/OFF, delay, kaunsa market timer chalaye
 *   What it does           → owner ke liye samjhaav
 *   Pending Result Tracker → declared events jinki bets abhi bhi bina result ke hain,
 *                            countdown ke saath; har 15 s refresh
 *   Result Source Links    → tracker par dikhne wali result websites ({match}/{market})
 */

const REFRESH_MS = 15000
const EMPTY_LINK = { name: '', url: '', sport_id: '', sort_order: 0, active: true }

const errText = (error) => error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Request failed'

const fmtMinutes = (sec) => {
  const n = Number(sec)
  if (!Number.isFinite(n) || n <= 0) return '—'
  if (n % 60 === 0) return `${n / 60} min`
  return `${(n / 60).toFixed(1)} min`
}

const fmtCountdown = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (v) => String(v).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
}

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—')

const ANCHOR_LABEL = { MATCH_ODDS: 'Match Odds', BOOKMAKER: 'Bookmaker' }

// {match}/{market} bharke final URL. Har value encode hoti hai, template wahi rehta hai.
const fillLink = (template, match, market) =>
  String(template || '')
    .replace(/\{match\}/g, encodeURIComponent(match || ''))
    .replace(/\{market\}/g, encodeURIComponent(market || ''))

const isMarketLink = (link) => /\{market\}/.test(link.url || '')

function AutoVoid() {
  // ── settings ────────────────────────────────────────────────────────────────
  const [cfg, setCfg] = useState(null)
  const [form, setForm] = useState({ enabled: false, delay_seconds: 3600, on_match_odds: true, on_bookmaker: true })
  const [saving, setSaving] = useState(false)

  // ── tracker ─────────────────────────────────────────────────────────────────
  const [pending, setPending] = useState(null)
  const [pendingLoading, setPendingLoading] = useState(false)
  const [clockOffset, setClockOffset] = useState(0)
  const [now, setNow] = useState(Date.now())

  // ── links ───────────────────────────────────────────────────────────────────
  const [links, setLinks] = useState([])
  const [newLink, setNewLink] = useState(EMPTY_LINK)
  const [adding, setAdding] = useState(false)
  const [editId, setEditId] = useState(null)
  const [editDraft, setEditDraft] = useState(EMPTY_LINK)
  const [busyLinkId, setBusyLinkId] = useState(null)

  const minDelay = cfg?.min_delay_seconds ?? 30
  const maxDelay = cfg?.max_delay_seconds ?? 86400

  const loadConfig = useCallback(async () => {
    try {
      const res = await autoVoidService.getConfig()
      if (res.success) {
        setCfg(res.data)
        setForm({
          enabled: !!res.data.enabled,
          delay_seconds: res.data.delay_seconds,
          on_match_odds: !!res.data.on_match_odds,
          on_bookmaker: !!res.data.on_bookmaker,
        })
      }
    } catch (error) {
      Toast.fire({ icon: 'error', title: errText(error) })
    }
  }, [])

  const loadPending = useCallback(async () => {
    setPendingLoading(true)
    try {
      const res = await autoVoidService.getPending()
      if (res.success) {
        setPending(res.data)
        // Countdown server ki ghadi se chale, browser ki nahi.
        if (res.data.server_time) setClockOffset(new Date(res.data.server_time).getTime() - Date.now())
      }
    } catch (error) {
      Toast.fire({ icon: 'error', title: errText(error) })
    } finally {
      setPendingLoading(false)
    }
  }, [])

  const loadLinks = useCallback(async () => {
    try {
      const res = await autoVoidService.getLinks()
      if (res.success) setLinks(res.data || [])
    } catch (error) {
      Toast.fire({ icon: 'error', title: errText(error) })
    }
  }, [])

  useEffect(() => {
    loadConfig()
    loadPending()
    loadLinks()
  }, [loadConfig, loadPending, loadLinks])

  // Tracker har 15 s refresh; countdown har second.
  const loadPendingRef = useRef(loadPending)
  loadPendingRef.current = loadPending
  useEffect(() => {
    const poll = setInterval(() => loadPendingRef.current(), REFRESH_MS)
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => { clearInterval(poll); clearInterval(tick) }
  }, [])

  // ── settings save ───────────────────────────────────────────────────────────
  const delayNum = Number(form.delay_seconds)
  const delayValid = Number.isInteger(delayNum) && delayNum >= minDelay && delayNum <= maxDelay
  const dirty = !!cfg && (
    form.enabled !== !!cfg.enabled ||
    delayNum !== Number(cfg.delay_seconds) ||
    form.on_match_odds !== !!cfg.on_match_odds ||
    form.on_bookmaker !== !!cfg.on_bookmaker
  )

  const saveConfig = async () => {
    if (!delayValid) {
      Toast.fire({ icon: 'error', title: `Delay must be a whole number between ${minDelay} and ${maxDelay} seconds` })
      return
    }
    if (form.enabled && !form.on_match_odds && !form.on_bookmaker) {
      Toast.fire({ icon: 'error', title: 'Pick at least one market (Match Odds or Bookmaker) to start the timer' })
      return
    }
    // ON karna paisa hilata hai: delay pehle hi khatam ho chuka ho to agla scan turant void karega.
    if (form.enabled && !cfg?.enabled) {
      const overdue = (pending?.events || []).filter(
        (ev) => new Date(ev.anchor_seen_at).getTime() + delayNum * 1000 <= now + clockOffset
      )
      const { isConfirmed } = await Swal.fire({
        icon: 'warning',
        title: 'Turn auto void ON?',
        html: overdue.length
          ? `<b>${overdue.length}</b> declared event(s) have already waited longer than ${fmtMinutes(delayNum)}. `
            + 'Their open bets with no result will be voided and refunded on the next scan.'
          : `Open bets with no result will be voided ${fmtMinutes(delayNum)} after the feed declares the event.`,
        showCancelButton: true,
        confirmButtonText: 'Turn ON',
      })
      if (!isConfirmed) return
    }
    setSaving(true)
    try {
      const res = await autoVoidService.saveConfig({
        enabled: form.enabled,
        delay_seconds: delayNum,
        on_match_odds: form.on_match_odds,
        on_bookmaker: form.on_bookmaker,
      })
      if (res.success) {
        Toast.fire({ icon: 'success', title: res.message || 'Auto void settings saved' })
        setCfg(res.data)
        loadPending()
      } else {
        Toast.fire({ icon: 'error', title: res.error || 'Failed to save' })
      }
    } catch (error) {
      Toast.fire({ icon: 'error', title: errText(error) })
    } finally {
      setSaving(false)
    }
  }

  // ── links CRUD ──────────────────────────────────────────────────────────────
  const toPayload = (l) => ({
    name: l.name,
    url: l.url,
    sport_id: l.sport_id ? String(l.sport_id) : null,
    sort_order: Number(l.sort_order) || 0,
    active: !!l.active,
  })

  const addLink = async () => {
    if (!newLink.name.trim() || !newLink.url.trim()) {
      Toast.fire({ icon: 'error', title: 'Name and URL are required' })
      return
    }
    setAdding(true)
    try {
      const res = await autoVoidService.createLink(toPayload(newLink))
      if (res.success) {
        Toast.fire({ icon: 'success', title: 'Link added' })
        setNewLink(EMPTY_LINK)
        loadLinks()
      }
    } catch (error) {
      Toast.fire({ icon: 'error', title: errText(error) })
    } finally {
      setAdding(false)
    }
  }

  const saveEdit = async () => {
    setBusyLinkId(editId)
    try {
      const res = await autoVoidService.updateLink(editId, toPayload(editDraft))
      if (res.success) {
        Toast.fire({ icon: 'success', title: 'Link saved' })
        setEditId(null)
        loadLinks()
      }
    } catch (error) {
      Toast.fire({ icon: 'error', title: errText(error) })
    } finally {
      setBusyLinkId(null)
    }
  }

  const toggleActive = async (link) => {
    setBusyLinkId(link.id)
    try {
      const res = await autoVoidService.updateLink(link.id, { active: !link.active })
      if (res.success) loadLinks()
    } catch (error) {
      Toast.fire({ icon: 'error', title: errText(error) })
    } finally {
      setBusyLinkId(null)
    }
  }

  const removeLink = async (link) => {
    const { isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: `Delete "${link.name}"?`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
    })
    if (!isConfirmed) return
    setBusyLinkId(link.id)
    try {
      const res = await autoVoidService.deleteLink(link.id)
      if (res.success) {
        Toast.fire({ icon: 'success', title: 'Link deleted' })
        loadLinks()
      }
    } catch (error) {
      Toast.fire({ icon: 'error', title: errText(error) })
    } finally {
      setBusyLinkId(null)
    }
  }

  // ── tracker helpers ─────────────────────────────────────────────────────────
  const activeLinks = useMemo(() => links.filter((l) => l.active), [links])
  const linksFor = (sportId, perMarket) =>
    activeLinks.filter((l) => (!l.sport_id || String(l.sport_id) === String(sportId)) && isMarketLink(l) === perMarket)

  const events = pending?.events || []
  const trackerOn = pending ? pending.enabled : !!cfg?.enabled
  const serverNow = now + clockOffset

  const sportOptions = (
    <>
      <option value="">All sports</option>
      {SPORTS_LIST.map(([id, name]) => (
        <option key={id} value={id}>{name}</option>
      ))}
    </>
  )

  return (
    <Layout title="Auto Void - Diamond Admin">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Auto Void</h4>
            {cfg && (
              <span className={`badge ${cfg.enabled ? 'bg-success' : 'bg-secondary'} font-size-12`}>
                {cfg.enabled ? 'ON' : 'OFF'}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        {/* ── Auto Void Settings ── */}
        <div className="col-lg-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Auto Void Settings</h5>
              {!cfg ? (
                <div className="text-center p-4">Loading...</div>
              ) : (
                <>
                  <div className="form-check form-switch mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="av-enabled"
                      role="switch"
                      checked={form.enabled}
                      onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="av-enabled">Enable auto void</label>
                  </div>

                  <label className="form-label" htmlFor="av-delay">Void after (seconds)</label>
                  <div className="input-group">
                    <input
                      type="number"
                      id="av-delay"
                      className={`form-control ${delayValid ? '' : 'is-invalid'}`}
                      min={minDelay}
                      max={maxDelay}
                      step="1"
                      value={form.delay_seconds}
                      onChange={(e) => setForm((f) => ({ ...f, delay_seconds: e.target.value }))}
                    />
                    <span className="input-group-text">= {fmtMinutes(form.delay_seconds)}</span>
                  </div>
                  <small className="text-muted d-block mt-1">
                    Counted from the moment the results cron first sees the Match Odds / Bookmaker result.
                    Allowed {minDelay}–{maxDelay} seconds (600 = 10 minutes).
                  </small>

                  <div className="mt-3 mb-1 fw-medium">Start the timer when the feed declares</div>
                  <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                    <div className="form-check mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="av-mo"
                        checked={form.on_match_odds}
                        onChange={(e) => setForm((f) => ({ ...f, on_match_odds: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="av-mo">Match Odds</label>
                    </div>
                    <div className="form-check mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="av-bm"
                        checked={form.on_bookmaker}
                        onChange={(e) => setForm((f) => ({ ...f, on_bookmaker: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="av-bm">Bookmaker</label>
                    </div>
                  </div>
                  <small className="text-muted d-block mt-1">
                    Whichever ticked market the feed settles (or voids) first starts the clock.
                  </small>

                  <button
                    type="button"
                    className="btn btn-primary mt-3"
                    disabled={!dirty || saving}
                    onClick={saveConfig}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── What it does ── */}
        <div className="col-lg-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">What it does</h5>
              <p>The results cron watches every event that still has open bets.</p>
              <p>
                When the feed declares the event&apos;s <b>Match Odds</b> or <b>Bookmaker</b> result,
                the timer for that event starts.
              </p>
              <p>
                After the delay, every bet on that event that is still <b>open with no result</b> (fancy
                sessions, other markets the feed never declared) is voided: exposure is released and the
                stake returns to the player&apos;s cash.
              </p>
              <p>
                Bets whose result has arrived are left to settlement. Bets you are settling by hand
                (manual status) are never touched.
              </p>
              <div className="alert alert-info mb-2">
                Every auto void appears in <b>Settlement → Total Bets</b> as VOID, and in the void audit as
                type <code>AUTO_TIMEOUT</code> with the anchor market and time in the note.
              </div>
              <div className="alert alert-warning mb-0">
                <b>Careful:</b> a market the feed settled under a name our matcher does not recognise counts
                as &quot;no result&quot; and will be refunded, not paid. Keep the delay long enough for late
                fancy results to land.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Pending Result Tracker ── */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-start justify-content-between mb-2">
                <h5 className="card-title mb-0">
                  Pending Result Tracker{' '}
                  <span className={`badge ${events.length ? 'bg-danger' : 'bg-success'} rounded-pill`}>
                    {events.length}
                  </span>
                </h5>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={pendingLoading}
                  onClick={loadPending}
                >
                  {pendingLoading ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
              <p className="text-muted">
                Match Odds / Bookmaker already declared, but these bets still have no result. Find the result
                and settle them in <Link to="/admin/settlement/fancy">Fancy</Link> /{' '}
                <Link to="/admin/settlement/match">Match</Link> Settlement before the countdown ends.{' '}
                {trackerOn
                  ? 'Auto Void is ON, so whatever is still open when the countdown ends will be voided.'
                  : 'Auto Void is OFF, so nothing will void them.'}{' '}
                Refreshes every 15 s.
              </p>

              {!pending ? (
                <div className="text-center p-3">Loading...</div>
              ) : events.length === 0 ? (
                <div className="text-success">All clear: no declared event has unsettled bets.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 220 }}>Event / Market</th>
                        <th>Sport</th>
                        <th>Declared</th>
                        <th className="text-end">Bets</th>
                        <th className="text-end">Users</th>
                        <th className="text-end">Stake</th>
                        <th>Voids in</th>
                        <th style={{ minWidth: 160 }}>Find result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((ev) => {
                        const left = new Date(ev.fire_at).getTime() - serverNow
                        const eventLinks = linksFor(ev.sport_id, false)
                        const marketLinks = linksFor(ev.sport_id, true)
                        const stake = ev.markets.reduce((sum, m) => sum + m.stake, 0)
                        return (
                          <Fragment key={ev.eventid}>
                            <tr className="table-light">
                              <td>
                                <b>{ev.match_title || ev.eventid}</b>
                                <small className="text-muted ms-1">#{ev.eventid}</small>
                                {ev.last_error && (
                                  <small className="text-danger d-block">Last void failed: {ev.last_error}</small>
                                )}
                              </td>
                              <td>{ev.sport_name || '—'}</td>
                              <td>
                                <small>
                                  {ANCHOR_LABEL[ev.anchor_market] || ev.anchor_market} {ev.anchor_status}
                                  <span className="d-block text-muted">{fmtTime(ev.anchor_seen_at)}</span>
                                </small>
                              </td>
                              <td className="text-end">{ev.bets}</td>
                              <td className="text-end">{ev.users}</td>
                              <td className="text-end">{stake.toFixed(2)}</td>
                              <td>
                                {!trackerOn ? (
                                  <span className="badge bg-secondary">Auto Void OFF</span>
                                ) : left > 0 ? (
                                  <span className={`badge ${left < 5 * 60 * 1000 ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                    {fmtCountdown(left)}
                                  </span>
                                ) : (
                                  <span className="badge bg-danger">voiding on next scan</span>
                                )}
                              </td>
                              <td>
                                <div className="d-flex flex-wrap" style={{ gap: 4 }}>
                                  {eventLinks.map((l) => (
                                    <a
                                      key={l.id}
                                      href={fillLink(l.url, ev.match_title)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline-primary py-0"
                                    >
                                      {l.name}
                                    </a>
                                  ))}
                                </div>
                              </td>
                            </tr>
                            {ev.markets.map((m) => (
                              <tr key={m.key}>
                                <td className="ps-4">
                                  {m.name}
                                  <small className="text-muted ms-1">
                                    ({m.fancy ? 'Fancy' : m.game_type})
                                  </small>
                                  <Link
                                    to={m.fancy ? '/admin/settlement/fancy' : '/admin/settlement/match'}
                                    className="ms-2 font-size-12"
                                  >
                                    settle
                                  </Link>
                                </td>
                                <td />
                                <td />
                                <td className="text-end">{m.bets}</td>
                                <td className="text-end">{m.users}</td>
                                <td className="text-end">{m.stake.toFixed(2)}</td>
                                <td />
                                <td>
                                  <div className="d-flex flex-wrap" style={{ gap: 4 }}>
                                    {marketLinks.map((l) => (
                                      <a
                                        key={l.id}
                                        href={fillLink(l.url, ev.match_title, m.name)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-secondary py-0"
                                      >
                                        {l.name}
                                      </a>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Result Source Links ── */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-2">Result Source Links</h5>
              <p className="text-muted">
                Websites the team opens from the tracker to find results. Put <code>{'{match}'}</code> in the
                URL to insert the event name, and <code>{'{market}'}</code> for the market name. A link with{' '}
                <code>{'{market}'}</code> shows on every market row (e.g.{' '}
                <code>{'https://www.google.com/search?q={match}+{market}'}</code>); the rest show once per event.
              </p>

              <div className="row g-2 align-items-end mb-3">
                <div className="col-md-2">
                  <label className="form-label mb-1" htmlFor="rsl-name">Name</label>
                  <input
                    id="rsl-name"
                    className="form-control form-control-sm"
                    placeholder="Cricbuzz"
                    value={newLink.name}
                    onChange={(e) => setNewLink((l) => ({ ...l, name: e.target.value }))}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label mb-1" htmlFor="rsl-url">URL</label>
                  <input
                    id="rsl-url"
                    className="form-control form-control-sm"
                    placeholder="https://www.cricbuzz.com/..."
                    value={newLink.url}
                    onChange={(e) => setNewLink((l) => ({ ...l, url: e.target.value }))}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label mb-1" htmlFor="rsl-sport">Sport</label>
                  <select
                    id="rsl-sport"
                    className="form-select form-select-sm"
                    value={newLink.sport_id}
                    onChange={(e) => setNewLink((l) => ({ ...l, sport_id: e.target.value }))}
                  >
                    {sportOptions}
                  </select>
                </div>
                <div className="col-md-1">
                  <label className="form-label mb-1" htmlFor="rsl-order">Order</label>
                  <input
                    id="rsl-order"
                    type="number"
                    min="0"
                    className="form-control form-control-sm"
                    value={newLink.sort_order}
                    onChange={(e) => setNewLink((l) => ({ ...l, sort_order: e.target.value }))}
                  />
                </div>
                <div className="col-md-1">
                  <div className="form-check mb-1">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="rsl-active"
                      checked={newLink.active}
                      onChange={(e) => setNewLink((l) => ({ ...l, active: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="rsl-active">Active</label>
                  </div>
                </div>
                <div className="col-md-2">
                  <button type="button" className="btn btn-sm btn-primary" disabled={adding} onClick={addLink}>
                    {adding ? 'Adding…' : 'Add link'}
                  </button>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>URL</th>
                      <th>Sport</th>
                      <th>Shows</th>
                      <th className="text-end">Order</th>
                      <th className="text-center">Active</th>
                      <th style={{ width: 140 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {links.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted">no records found</td>
                      </tr>
                    ) : links.map((link) => {
                      const editing = editId === link.id
                      const busy = busyLinkId === link.id
                      if (editing) {
                        return (
                          <tr key={link.id} className="table-warning">
                            <td>
                              <input className="form-control form-control-sm" value={editDraft.name}
                                onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))} />
                            </td>
                            <td>
                              <input className="form-control form-control-sm" value={editDraft.url}
                                onChange={(e) => setEditDraft((d) => ({ ...d, url: e.target.value }))} />
                            </td>
                            <td>
                              <select className="form-select form-select-sm" value={editDraft.sport_id || ''}
                                onChange={(e) => setEditDraft((d) => ({ ...d, sport_id: e.target.value }))}>
                                {sportOptions}
                              </select>
                            </td>
                            <td>{isMarketLink(editDraft) ? 'Each market' : 'Each event'}</td>
                            <td>
                              <input type="number" min="0" className="form-control form-control-sm text-end"
                                style={{ maxWidth: 80, marginLeft: 'auto' }} value={editDraft.sort_order}
                                onChange={(e) => setEditDraft((d) => ({ ...d, sort_order: e.target.value }))} />
                            </td>
                            <td className="text-center">
                              <input type="checkbox" className="form-check-input" checked={!!editDraft.active}
                                onChange={(e) => setEditDraft((d) => ({ ...d, active: e.target.checked }))} />
                            </td>
                            <td className="text-nowrap">
                              <button type="button" className="btn btn-sm btn-success me-1" disabled={busy} onClick={saveEdit}>
                                Save
                              </button>
                              <button type="button" className="btn btn-sm btn-light" disabled={busy} onClick={() => setEditId(null)}>
                                Cancel
                              </button>
                            </td>
                          </tr>
                        )
                      }
                      return (
                        <tr key={link.id}>
                          <td className="fw-medium">{link.name}</td>
                          <td className="text-break">{link.url}</td>
                          <td>{link.sport_name}</td>
                          <td>{isMarketLink(link) ? 'Each market' : 'Each event'}</td>
                          <td className="text-end">{link.sort_order}</td>
                          <td className="text-center">
                            <button
                              type="button"
                              className={`btn btn-sm ${link.active ? 'btn-success' : 'btn-secondary'}`}
                              disabled={busy}
                              onClick={() => toggleActive(link)}
                            >
                              {link.active ? 'On' : 'Off'}
                            </button>
                          </td>
                          <td className="text-nowrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-1"
                              disabled={busy}
                              onClick={() => {
                                setEditId(link.id)
                                setEditDraft({ ...link, sport_id: link.sport_id || '' })
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled={busy}
                              onClick={() => removeLink(link)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default AutoVoid
